import { useMemo } from 'react';

import { Campaign } from '@/lib/graphql/generated/graphql';
import { useQuery } from '@apollo/client/react';

import { GetPreviousCampaignsRevenueDocument } from '../graphql/generated/graphql';

/**
 * Enriches previous campaigns with their GMV, read from the data warehouse.
 *
 * GMV is deliberately kept out of `GetDealById`: the warehouse is orders of magnitude slower than
 * the rest of the deal, and GMV is only ever displayed in the campaign selector. Fetching it here
 * means it is requested when the analysis sidebar opens — not on every deal page load.
 *
 * `no-cache` keeps this partial campaign list from overwriting the full one already held for the
 * deal: campaigns are not normalised, so Apollo would replace the cached array wholesale and drop
 * the names and dates that came with `GetDealById`.
 */
export const usePreviousCampaignsRevenue = (
  dealId: string,
  campaigns: Campaign[]
) => {
  const { data, loading } = useQuery(GetPreviousCampaignsRevenueDocument, {
    variables: { dealId },
    // Brands with no past campaign have no GMV to show, and the warehouse should not be asked.
    skip: !dealId || campaigns.length === 0,
    fetchPolicy: 'no-cache',
  });

  /**
   * Campaigns are returned as soon as they are known, with GMV filled in once it arrives, so the
   * selector renders without waiting on the warehouse.
   */
  const campaignsWithRevenue = useMemo(() => {
    const revenueBySaleId = new Map(
      (data?.deal?.latestPreviousCampaigns ?? []).map((campaign) => [
        campaign.saleId,
        campaign.revenue ?? null,
      ])
    );

    return campaigns.map((campaign) => ({
      ...campaign,
      revenue: revenueBySaleId.get(campaign.saleId) ?? null,
    }));
  }, [campaigns, data]);

  return { campaignsWithRevenue, loading };
};
